import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Minus, ChevronUp, ChevronDown, PlusCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipeSchema, RecipeInput } from '../../schemas/recipe';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { CategoryModal } from '../category/CategoryModal';
import { getCategories, addCategory, updateCategory } from '../../services/category';

interface RecipeFormProps {
  initialData?: RecipeInput;
  onSubmit: (data: RecipeInput) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const measurementUnits = [
  { value: 'cup', label: 'cups' },
  { value: 'tsp', label: 'teaspoons' },
  { value: 'tbsp', label: 'tablespoons' },
  { value: 'oz', label: 'ounces' },
  { value: 'lb', label: 'pounds' },
  { value: 'ml', label: 'milliliters' },
  { value: 'fl oz', label: 'fluid ounces' },
  { value: 'g', label: 'grams' },
  { value: 'piece', label: 'pieces' },
  { value: 'whole', label: 'whole' },
];

export function RecipeForm({
  initialData,
  onSubmit,
  isLoading,
  disabled,
}: RecipeFormProps) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const categoryMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      if (selectedCategory) {
        return updateCategory(selectedCategory, data.name);
      }
      return addCategory(data.name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsModalOpen(false);
      toast.success(selectedCategory ? 'Category updated!' : 'Category created!');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save category');
    },
  });

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<RecipeInput>({
    resolver: zodResolver(recipeSchema),
    defaultValues: {
      ingredients: [{ name: '', quantity: 1, unit: '' }],
      instructions: [''],
      cookingTime: 0,
      prepTime: 0,
      servings: 1,
      dietaryTags: [],
      difficulty: 'medium',
    },
  });

  // Reset form when initialData changes
  React.useEffect(() => {
    if (initialData) {
      console.log('Resetting form with initial data:', initialData);
      reset(initialData);
    }
  }, [initialData, reset]);

  const handleFormSubmit = async (data: RecipeInput) => {
    console.log('--- Recipe Form: Submission Started ---', {
      timestamp: new Date().toISOString(),
      formData: data
    });

    try {
      const validatedData = recipeSchema.parse(data);
      console.log('Form validation successful', {
        timestamp: new Date().toISOString(),
        validatedData
      });
      
      onSubmit(validatedData);
    } catch (error) {
      console.error('Form validation failed:', {
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      if (error instanceof z.ZodError) {
        console.error('Zod validation errors:', {
          errors: error.errors.map(err => ({
            path: err.path.join('.'),
            message: err.message
          })),
          timestamp: new Date().toISOString()
        });
        
        error.errors.forEach(err => {
          toast.error(`${err.path.join('.')}: ${err.message}`);
        });
        return;
      }
      throw error;
    }
  };

  const {
    fields: ingredientFields,
    append: appendIngredient,
    remove: removeIngredient,
    move: moveIngredient,
  } = useFieldArray({
    control,
    name: 'ingredients',
  });

  const {
    fields: instructionFields,
    append: appendInstruction,
    remove: removeInstruction,
    move: moveInstruction,
  } = useFieldArray({
    control,
    name: 'instructions',
  });

  return (
    <form 
      onSubmit={handleSubmit(handleFormSubmit)} 
      className="space-y-8"
      noValidate
    >
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Input
            label="Recipe Title"
            {...register('title')}
            error={errors.title?.message}
            disabled={disabled}
            aria-describedby={errors.title ? 'title-error' : undefined}
          />
          {errors.title && (
            <p id="title-error" className="mt-1 text-sm text-red-600">
              {errors.title.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setSelectedCategory(null);
                setIsModalOpen(true);
              }}
              className="!p-1"
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
          <Select
            options={[
              ...categories.map((category) => ({
                key: category.id,
                value: category.name,
                label: category.name
              })),
            ]}
            {...register('category')}
            error={errors.category?.message}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <Input
          type="number"
          disabled={disabled}
          label="Prep Time (minutes)"
          {...register('prepTime', { valueAsNumber: true })}
          error={errors.prepTime?.message}
        />
        <Input
          type="number"
          disabled={disabled}
          label="Cooking Time (minutes)"
          defaultValue="15"
          {...register('cookingTime', { valueAsNumber: true })}
          error={errors.cookingTime?.message}
        />
        <Input
          type="number"
          disabled={disabled}
          label="Servings"
          {...register('servings', { valueAsNumber: true })}
          error={errors.servings?.message}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ingredients
        </label>
        <div className="space-y-4">
          {ingredientFields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Ingredient name"
                  {...register(`ingredients.${index}.name`)}
                  disabled={disabled}
                  error={errors.ingredients?.[index]?.name?.message}
                />
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  placeholder="Qty"
                  {...register(`ingredients.${index}.quantity`, {
                    valueAsNumber: true,
                  })}
                  disabled={disabled}
                  error={errors.ingredients?.[index]?.quantity?.message}
                />
              </div>
              <div className="w-24">
                <Input
                  placeholder="Unit"
                  {...register(`ingredients.${index}.unit`)}
                  disabled={disabled}
                  error={errors.ingredients?.[index]?.unit?.message}
                  list={`units-${index}`}
                />
                <datalist id={`units-${index}`}>
                  {measurementUnits.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </datalist>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={disabled || index === 0}
                  onClick={() => moveIngredient(index, index - 1)}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={disabled || index === ingredientFields.length - 1}
                  onClick={() => moveIngredient(index, index + 1)}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  disabled={disabled || ingredientFields.length === 1}
                  onClick={() => removeIngredient(index)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            size="sm"
            onClick={() => appendIngredient({ name: '', quantity: 1, unit: '' })}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Ingredient
          </Button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Instructions
        </label>
        <div className="space-y-4">
          {instructionFields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium">
                {index + 1}
              </span>
              <div className="flex-1">
                <Input
                  placeholder="Instruction step"
                  {...register(`instructions.${index}`)}
                  disabled={disabled}
                  error={errors.instructions?.[index]?.message}
                />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={disabled || index === 0}
                  onClick={() => moveInstruction(index, index - 1)}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={disabled || index === instructionFields.length - 1}
                  onClick={() => moveInstruction(index, index + 1)}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  disabled={disabled || instructionFields.length === 1}
                  onClick={() => removeInstruction(index)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            size="sm"
            onClick={() => appendInstruction('')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Step
          </Button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Difficulty
        </label>
        <div className="flex gap-4">
          {['easy', 'medium', 'hard'].map((level) => (
            <label
              key={level}
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type="radio"
                value={level}
                disabled={disabled}
                {...register('difficulty')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700 capitalize">{level}</span>
            </label>
          ))}
        </div>
        {errors.difficulty && (
          <p className="text-sm text-red-600 mt-1">{errors.difficulty.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dietary Tags
        </label>
        <Input
          placeholder="Enter tags separated by commas or spaces (e.g., vegan, raw, gluten-free)"
          {...register('dietaryTags')}
          disabled={disabled}
          error={errors.dietaryTags?.message}
          helperText="Optional: Leave empty or enter tags separated by commas or spaces"
        />
      </div>

      <div className="flex justify-end">
        <Button 
          type="submit" 
          isLoading={isLoading || disabled}
          disabled={isLoading || disabled}
          aria-disabled={disabled}
        >
          {initialData ? 'Update Recipe' : 'Create Recipe'}
        </Button>
      </div>
      
      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCategory(null);
        }}
        onSubmit={categoryMutation.mutateAsync}
        isLoading={categoryMutation.isPending}
      />
    </form>
  );
}